import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { I18N_PREFIX, KNOWLEDGE_ARTICLE_PATH } from './RetrievedChunkList.constants';
import type { RetrievedChunkListProps, RetrievedChunkRowProps } from './RetrievedChunkList.types';
import { withKeys } from './RetrievedChunkList.utils';

const Label = ({ chunk }: { chunk: RetrievedChunkRowProps['chunk'] }) => (
	<span className="min-w-0 flex-1 truncate">{chunk.title || chunk.fileName || chunk.chunkId}</span>
);

/**
 * One retrieved chunk.
 *
 * The link is conditional but the evidence never is: whoever can read the
 * conversation may know where the answer came from. What is gated is the
 * navigation, because the knowledge console sits behind `(p) => !!p.update`
 * and `ProtectedRoute` renders a full-page `NoAccess` rather than a quiet 403 —
 * so an ungated link would throw the operator out of the conversation they were
 * reading. Today only one person holds `update`, so this is the common path,
 * not the edge case.
 */
const Row = ({ chunk, canNavigate }: RetrievedChunkRowProps) => {
	const { t } = useTranslation('translation', { keyPrefix: I18N_PREFIX });
	const linkable = canNavigate && !!chunk.sourceId;

	return (
		<li className="flex items-center gap-2xs px-xs py-3xs font-sans text-[12px] text-default">
			<span className="w-[18px] shrink-0 text-right font-mono text-[11px] text-subtle tabular-nums">{chunk.rank}</span>
			<FileText className="h-[12px] w-[12px] shrink-0 text-subtle" aria-hidden />
			{linkable ? (
				<Link
					to={`${KNOWLEDGE_ARTICLE_PATH}/${chunk.sourceId}`}
					className="min-w-0 flex-1 truncate text-purple-600 underline-offset-2 hover:underline"
					title={t('EVIDENCE_OPEN_ARTICLE')}
				>
					{chunk.title || chunk.fileName || chunk.chunkId}
				</Link>
			) : (
				<Label chunk={chunk} />
			)}
			{chunk.score != null && (
				<span className="shrink-0 font-mono text-[11px] text-subtle tabular-nums" title={t('EVIDENCE_SCORE')}>
					{chunk.score.toFixed(2)}
				</span>
			)}
		</li>
	);
};

/**
 * The chunks a KB search actually returned, in retrieval order.
 *
 * This is what turns "this answer is wrong" into "edit *this* article" — the
 * bridge to the knowledge console. Before `back#SPEC-010` the event only
 * carried titles, and titles do not identify: 380 distinct titles for 381
 * articles on the live collection.
 */
export const RetrievedChunkList = ({ chunks, canNavigate }: RetrievedChunkListProps) => {
	const { t } = useTranslation('translation', { keyPrefix: I18N_PREFIX });

	if (chunks.length === 0) {
		return <p className="px-xs py-3xs font-sans text-[12px] text-subtle">{t('EVIDENCE_EMPTY')}</p>;
	}

	return (
		<div className="flex flex-col gap-3xs">
			<ol className="divide-y divide-subtle rounded-md bg-white" data-testid="retrieved-chunks">
				{withKeys(chunks).map(({ key, chunk }) => (
					<Row key={key} chunk={chunk} canNavigate={canNavigate} />
				))}
			</ol>
			{chunks[0]?.collection && (
				<p className="px-xs font-sans text-[10px] uppercase tracking-wider text-subtle">
					{t('EVIDENCE_COLLECTION')}: <span className="font-mono normal-case">{chunks[0].collection}</span>
				</p>
			)}
		</div>
	);
};
